import type {
  LayerAiAppliedChange,
  LayerAiAppliedStep,
} from "@/v2-backoffice-client/layer/ai/layer.ai.types"

/**
 * Human summaries for the applied-changes timeline. The assistant's own
 * business summary ("Turnover heatmap replacing point clusters") wins when
 * present; the key-diff fallback ("Configuration — renderer, popupTemplate")
 * still says more than a bare field name.
 */

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

/** Top-level keys whose value differs between the two objects. */
function changedTopLevelKeys(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): string[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  const changed: string[] = []
  for (const key of keys) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      changed.push(key)
    }
  }
  return changed
}

/**
 * One change's fallback summary, derived from its recorded before/after
 * values: for object blobs the changed top-level keys ("Configuration —
 * renderer, labelingInfo +2 more"), for array blobs the entry count
 * ("Filters — 4 controls added").
 */
function describeAppliedChange(change: LayerAiAppliedChange): string {
  const before = safeParse(change.before)
  const after = safeParse(change.after)

  if (Array.isArray(after)) {
    const noun = change.field === "Filters" ? "control" : "entry"
    const plural = after.length === 1 ? noun : `${noun}s`
    const hadContent = Array.isArray(before) && before.length > 0
    return hadContent
      ? `${change.field} — now ${after.length} ${plural}`
      : `${change.field} — ${after.length} ${plural} added`
  }

  if (after == null) return `${change.field} — cleared`

  const isObjectPair =
    typeof before === "object" &&
    before !== null &&
    !Array.isArray(before) &&
    typeof after === "object"

  if (!isObjectPair) return `${change.field} — added`

  const changed = changedTopLevelKeys(
    before as Record<string, unknown>,
    after as Record<string, unknown>,
  )
  if (changed.length === 0) return `${change.field} — no changes`

  const shown = changed.slice(0, 3).join(", ")
  const more = changed.length > 3 ? ` +${changed.length - 3} more` : ""
  return `${change.field} — ${shown}${more}`
}

/**
 * One history entry's label: the assistant's business summary when it gave
 * one, otherwise the key-diff fallback (joined field names for a batch).
 */
export function describeAppliedStep(step: LayerAiAppliedStep): string {
  if (step.summary) return step.summary
  if (step.changes.length === 1) return describeAppliedChange(step.changes[0])
  return `${step.changes.map((change) => change.field).join(", ")} updated`
}
